import {
  Button,
  DotProgress,
  Icon,
  IconButton,
  TextField,
} from '@wandb/components';
import {clsx} from 'clsx';
import React, {useCallback, useState} from 'react';

import {
  clampToTitleLength,
  MAX_THREAD_TITLE_LENGTH,
  sanitizeThreadTitle,
  type Thread,
} from '../../../agentChat/store/thread';
import {TruncateTextTooltip} from '../../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {useNightModeRevertFilter} from '../useNightModeRevertFilter';
import {getProjectChipLabel} from './projectChip';

export type HistoryItemMode = 'idle' | 'renaming' | 'confirming-delete';

interface Props {
  thread: Thread;
  displayTitle: string;
  currentEntity: string | null;
  mode: HistoryItemMode;
  isSelected: boolean;
  isRunning: boolean;
  onSelect: (rootTurnId: string) => void;
  onStartRename: (threadId: string) => void;
  onCommitRename: (threadId: string, newTitle: string) => void;
  onStartDelete: (threadId: string) => void;
  onConfirmDelete: (threadId: string) => void;
  onCancelMode: () => void;
}

const CSS_CLASS_TITLE_AREA = 'min-w-0 flex-1';

/**
 * HistoryItem renders a single chat-history row. The `mode` prop toggles
 * between idle, inline-rename, and delete-confirmation; idle mode shows a
 * project chip when the row sits in a non-default project.
 */
export const HistoryItem: React.FC<Props> = ({
  thread,
  displayTitle,
  currentEntity,
  mode,
  isSelected,
  isRunning,
  onSelect,
  onStartRename,
  onCommitRename,
  onStartDelete,
  onConfirmDelete,
  onCancelMode,
}) => {
  const rootTurnId = thread.rootTurnId ?? thread.id;
  const isRenaming = mode === 'renaming';
  const isConfirmingDelete = mode === 'confirming-delete';
  const isInteractive = mode === 'idle';
  const chipLabel = getProjectChipLabel(thread, currentEntity);

  const handleSelect = useCallback(() => {
    onSelect(rootTurnId);
  }, [onSelect, rootTurnId]);

  const handleStartRename = useCallback(
    () => onStartRename(thread.id),
    [onStartRename, thread.id]
  );

  const handleCommitFromField = useCallback(
    (newTitle: string) => onCommitRename(thread.id, newTitle),
    [onCommitRename, thread.id]
  );

  const handleStartDelete = useCallback(
    () => onStartDelete(thread.id),
    [onStartDelete, thread.id]
  );

  const handleConfirmDelete = useCallback(
    () => onConfirmDelete(thread.id),
    [onConfirmDelete, thread.id]
  );

  // Escape cancels confirm-delete mode. `stopPropagation` prevents Escape
  // from bubbling up and closing the popover.
  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isConfirmingDelete && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancelMode();
      }
    },
    [isConfirmingDelete, onCancelMode]
  );

  return (
    <div
      onClick={isInteractive ? handleSelect : undefined}
      onKeyDown={handleRowKeyDown}
      className={clsx(
        'group flex rounded-[4px] px-8 py-4 gap-8 items-center self-stretch',
        isInteractive && 'cursor-pointer hover:bg-moon-100',
        isSelected && 'bg-teal-300/[0.32]'
      )}>
      <div className="flex flex-1 flex-col min-w-0">
        <div className="flex h-[24px] items-center gap-[8px]">
          {isRenaming ? (
            <RenameField
              initialTitle={displayTitle}
              onCommit={handleCommitFromField}
              onCancel={onCancelMode}
            />
          ) : (
            <>
              <div
                className={clsx(
                  CSS_CLASS_TITLE_AREA,
                  // Mirror TextField's input border + horizontal padding so the
                  // title text doesn't shift when toggling into rename mode.
                  'text-sm font-normal leading-[19.6px]',
                  isSelected && '!font-medium text-teal-600'
                )}>
                <TruncateTextTooltip
                  fullText={displayTitle}
                  truncationType={TruncationType.End}
                  noLink
                />
              </div>
            </>
          )}
        </div>
        {!isRenaming && !isConfirmingDelete && chipLabel != null && (
          <div
            className={clsx(
              'flex items-center gap-[4px] text-sm leading-5 text-moon-500',
              isSelected && ' text-teal-600'
            )}>
            <Icon icon="folder-project" size="xs" />
            <span className="min-w-0 truncate">{chipLabel}</span>
          </div>
        )}
      </div>
      {isConfirmingDelete ? (
        <DeleteConfirmation
          onConfirm={handleConfirmDelete}
          onCancel={onCancelMode}
        />
      ) : (
        <>
          {isRunning && (
            <div
              className={clsx(
                'flex shrink-0 items-center text-moon-350',
                'group-hover:hidden group-focus-within:hidden'
              )}>
              <DotProgress aria-label="Chat is running" size="sm" />
            </div>
          )}
          {!isRenaming && !isConfirmingDelete && (
            <div
              className={clsx(
                'shrink-0 items-center gap-[2px]',
                'hidden group-hover:flex group-focus-within:flex'
              )}>
              <IconButton
                variant="ghost"
                size="sm"
                icon="pencil-edit"
                aria-label="Rename chat"
                onClick={handleStartRename}
              />
              <IconButton
                variant="ghost"
                size="sm"
                icon="delete"
                aria-label="Delete chat"
                onClick={handleStartDelete}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface DeleteConfirmationProps {
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * DeleteConfirmation renders the destructive Delete button with an adjacent
 * cancel icon. Auto-focuses the Delete button so Enter confirms.
 */
function DeleteConfirmation({onConfirm, onCancel}: DeleteConfirmationProps) {
  const focusOnMount = useCallback((el: HTMLElement | null) => el?.focus(), []);

  return (
    <div className="flex items-center gap-[2px]">
      <Button
        ref={focusOnMount}
        variant="destructive"
        size="sm"
        startIcon="delete"
        onPress={onConfirm}>
        Delete
      </Button>
      <IconButton
        variant="secondary"
        size="sm"
        icon="close"
        aria-label="Cancel delete"
        onClick={onCancel}
      />
    </div>
  );
}

interface RenameFieldProps {
  initialTitle: string;
  onCommit: (newTitle: string) => void;
  onCancel: () => void;
}

/**
 * RenameField owns the in-edit title value and is mounted only while
 * rename mode is active, so each open starts with a fresh input seeded
 * from `initialTitle`.
 */
function RenameField({initialTitle, onCommit, onCancel}: RenameFieldProps) {
  const [value, setValue] = useState(() => clampToTitleLength(initialTitle));
  const nightModeRevertFilter = useNightModeRevertFilter();

  // TODO: Drop once TextField in @wandb/components wires `autoFocus`
  // through. SearchField/Switch/SelectFieldContent already do.
  // Today the prop falls through to AriaTextField and is silently dropped.
  const focusOnMount = useCallback((el: HTMLDivElement | null) => {
    const input = el?.querySelector<HTMLInputElement>('input');
    input?.focus();
    input?.select();
  }, []);

  const commit = useCallback(() => {
    const sanitizedTitle = sanitizeThreadTitle(value);

    // An empty field is a deliberate request to clear the custom title and let
    // it fall back to a default; input that only coerces to empty (whitespace
    // or control characters) cancels instead, preserving the prior title.
    if (sanitizedTitle === '' && value !== '') {
      onCancel();
      return;
    }

    onCommit(sanitizedTitle);
  }, [onCommit, onCancel, value]);

  // Enter commits, Escape cancels. `stopPropagation` prevents Escape from
  // bubbling up and closing the popover.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // NOTE: Ignore keys fired mid-IME-composition so Enter/Escape select or
      // cancel a candidate instead of committing or closing the rename.
      if (e.nativeEvent.isComposing) {
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    },
    [commit, onCancel]
  );

  return (
    <>
      <div
        ref={focusOnMount}
        className={clsx(
          CSS_CLASS_TITLE_AREA,
          '[&_input]:!text-sm [&_input::selection]:text-white'
        )}
        style={{filter: nightModeRevertFilter}}>
        <TextField
          aria-label="Edit chat title"
          value={value}
          onChange={setValue}
          onKeyDown={handleKeyDown}
          // NOTE: This max length counts UTF-16 code units, unlike the wb_agent
          // backend which counts Unicode code points; titles with emojis or
          // other supplementary-plane characters may be capped slightly early.
          maxLength={MAX_THREAD_TITLE_LENGTH}
          placeholder="Enter a title"
          fullWidth
        />
      </div>
      <div className="flex items-center gap-[2px]">
        <IconButton
          variant="primary"
          size="sm"
          icon="checkmark"
          aria-label="Save chat title"
          onClick={commit}
        />
        <IconButton
          variant="secondary"
          size="sm"
          icon="close"
          aria-label="Cancel rename"
          onClick={onCancel}
        />
      </div>
    </>
  );
}
